/*
 * Roomix Help Centre: self-serve replacement part request (inside the order view).
 * Talks only to /apps/help/parts/*. The email, address, phone and photos are held in memory only:
 * never in a URL, storage, cookies or logs. All strings from the API are rendered with textContent.
 * Part names, order, pack grouping, reasons, photo rules and confirmation wording are server-driven.
 */
(function () {
  'use strict';

  var CONTACT_URL = 'https://www.roomix.com/pages/contact';
  var EP = { request: '/apps/help/parts/request', photo: '/apps/help/parts/photo' };
  var UK_PC = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;
  var MAX_PHOTOS = 5;
  var MAX_BYTES = 8 * 1024 * 1024;
  var STEPS = ['Items', 'Parts', 'Reason', 'Checks', 'Confirm'];
  var STEP_KEYS = ['items', 'parts', 'reasons', 'checks', 'confirm'];
  var RATE_COPY = 'Too many attempts. Please wait a few minutes and try again.';
  var ADDR_COPY = 'Please check the address. We need line 1, town or city and a valid UK postcode.';
  var INELIGIBLE_COPY = 'We can’t take this request online. Please contact us.';
  var BOX_PATHS = ['M3 8l9-5 9 5v8l-9 5-9-5z', 'M3 8l9 5 9-5M12 13v8'];

  var TITLES = {
    intro: 'Need a replacement part?',
    items: 'Replacement part', parts: 'Replacement part', reasons: 'Replacement part', checks: 'Replacement part', confirm: 'Replacement part',
    sent: 'We’ve got your request',
    error: 'We couldn’t send your request'
  };

  var PHOTO_ERR = {
    file_too_large: 'That photo is too large. Please choose one under 8 MB.',
    bad_file_type: 'Please choose a JPG, PNG or HEIC photo.',
    too_many_photos: 'You can add up to 5 photos.',
    rate_limited: RATE_COPY,
    not_found: 'We couldn’t upload that photo. Please try again.'
  };

  function core() { return window.RoomixHelpCore; }
  function el(tag, cls, text) { return core().el(tag, cls, text); }
  function svg(paths, size, o) { return core().svg(paths, size, o); }
  function str(v) { return core().str(v); }
  function safeUrl(u) { return core().safeUrl(u); }
  function isMock() { return core().isMock(); }
  function qs(n) { return core().qs(n); }

  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 3 | 8)).toString(16);
    });
  }
  function addrString(a) {
    return [a.line1, a.line2, a.city, a.postcode].map(function (x) { return str(x).trim(); }).filter(Boolean).join(', ');
  }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  // The badge shows the guide number. Omitted for HW, n/a, empty, or when the name already ends with that number.
  function badgeFor(p) {
    var n = str(p.partNo).trim();
    if (!n) return '';
    var low = n.toLowerCase();
    if (low === 'hw' || low === 'n/a') return '';
    var esc = n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp('(^|[\\s(#])' + esc + '\\)?$').test(str(p.name).trim())) return '';
    return n;
  }

  /* ---------- mock fixtures (dev theme only) ---------- */

  function mockParts(stage) {
    var preset = qs('parts');
    var ps = qs('partsstate');
    var partial = qs('shipment') === 'partial';
    var anyOut = stage === 'dispatched' || !!preset || partial;
    if (ps === 'none' || !anyOut) return { available: false, state: 'none' };
    var r = {
      available: true,
      state: 'open',
      responseTime: '2 working days',
      delivery: { line1: '14 Orchard Road', line2: '', city: 'London', postcode: 'E8 3AB', phone: null },
      items: [
        { id: 'it-bed', title: 'Transition Convertible House Bed', variant: 'Natural · UK Single (190 × 90 cm)', imageUrl: null,
          requestable: true, note: 'Dispatched Mon 5 Oct',
          parts: [
            { id: 'pt-back', partNo: '1', name: 'Back Panel', qty: 1, packNo: 1, packCount: 2 },
            { id: 'pt-side', partNo: '2', name: 'Side Panels', qty: 2, packNo: 1, packCount: 2 },
            { id: 'pt-hw1', partNo: 'HW', name: 'Fittings (Pack 1 of 2 - Bed Base)', qty: 1, packNo: 1, packCount: 2 },
            { id: 'pt-slat', partNo: '3', name: 'Slat Set', qty: 1, packNo: 2, packCount: 2 },
            { id: 'pt-brkt', partNo: '5', name: 'Corner Bracket 5', qty: 4, packNo: 2, packCount: 2 },
            { id: 'pt-key', partNo: 'n/a', name: 'Allen Key', qty: 1, packNo: 2, packCount: 2 },
            { id: 'pt-hw2', partNo: 'HW', name: 'Fittings (Pack 2 of 2 - Slats)', qty: 1, packNo: 2, packCount: 2 }
          ] },
        { id: 'it-drawers', title: 'Add-On Legs and Drawers', variant: 'Natural · UK Single (190 × 90 cm)', imageUrl: null,
          requestable: true, note: 'Dispatched Mon 5 Oct',
          parts: [
            { id: 'pt-dfront', partNo: '1', name: 'Drawer Front', qty: 1, packNo: null, packCount: 1 },
            { id: 'pt-drun', partNo: '2', name: 'Drawer Runners', qty: 2, packNo: null, packCount: 1 },
            { id: 'pt-dhw', partNo: 'HW', name: 'Drawer Fittings', qty: 1, packNo: null, packCount: 1 }
          ] },
        { id: 'it-table', title: 'Transition Bedside Table', variant: 'Natural · Single drawer', imageUrl: null,
          requestable: false, note: 'No part list for this product yet. Contact us and we’ll sort it.', parts: [] }
      ],
      reasons: [
        { code: 'missing', label: 'Missing from the box', requiresPhoto: true, kind: 'missing' },
        { code: 'damaged', label: 'Damaged on arrival', requiresPhoto: true, kind: 'damaged' },
        { code: 'faulty', label: 'Faulty or doesn’t fit', requiresPhoto: false, kind: null },
        { code: 'other', label: 'Something else', requiresPhoto: false, kind: null }
      ],
      confirmations: {
        version: 'mock-v1',
        items: [
          { key: 'accuracy', title: 'The details are right', text: 'I’ve checked the part names and numbers against my assembly guide, and the details I’ve given are correct.' },
          { key: 'boxes', title: 'I’ve checked every box', text: 'I’ve opened every box and the part really is missing.', onlyIfReasonKind: 'missing' },
          { key: 'damaged', title: 'Damaged parts', text: 'Sending damaged parts back is optional. If we need them, we’ll arrange it after the replacement arrives.', onlyIfReasonKind: 'damaged' }
        ]
      },
      previous: []
    };
    var sent = (window.ROOMIX_HELP_MOCK_PARTS || []).slice();
    if (ps === 'earlier' || preset === 'earlier') {
      sent.push({ reference: 'PR-1003', createdAt: 'Mon 5 Oct', status: 'SUBMITTED', statusLabel: 'Received',
        items: [{ title: 'Transition Convertible House Bed', parts: [{ name: 'Side Panels', qty: 1 }] }] });
      sent.push({ reference: 'PR-1001', createdAt: 'Fri 25 Sep', status: 'FULFILLED', statusLabel: 'Sent',
        items: [{ title: 'Add-On Legs and Drawers', parts: [{ name: 'Drawer Front', qty: 1 }, { name: 'Drawer Runners', qty: 2 }] }] });
    }
    r.previous = sent;
    return r;
  }

  function mockCall(kind, payload) {
    (window.ROOMIX_HELP_MOCK_LOG = window.ROOMIX_HELP_MOCK_LOG || []).push({ kind: 'parts-' + kind, requestId: payload && payload.requestId });
    var forced = qs('partserr');
    if (kind === 'photo') {
      var pe = qs('photoerr');
      return sleep(600).then(function () { return pe ? { status: 413, json: { ok: false, code: pe } } : { status: 200, json: { ok: true, photoId: 'pp-' + uuid().slice(0, 8) } }; });
    }
    return sleep(900).then(function () {
      if (forced) return { status: forced === 'request_failed' ? 502 : 409, json: { ok: false, code: forced } };
      var n = (window.ROOMIX_HELP_MOCK_PARTS || []).length;
      return { status: 200, json: { ok: true, reference: 'PR-' + (1004 + n), deliveryAddress: addrString(payload.delivery), hasPhotos: !!payload.__hasPhotos } };
    });
  }

  /* ---------- network ---------- */

  function send(kind, url, body, isForm) {
    if (isMock()) return mockCall(kind, body);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 45000) : null;
    var init = { method: 'POST', cache: 'no-store', credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined };
    if (isForm) { init.body = body; } else { init.headers = { 'Content-Type': 'application/json', Accept: 'application/json' }; init.body = JSON.stringify(body); }
    return fetch(url, init).then(function (res) {
      return res.json().then(function (j) { return { status: res.status, json: j }; }, function () { return { status: res.status, json: null }; });
    }).then(function (r) {
      if (timer) clearTimeout(timer);
      return r;
    }, function () {
      if (timer) clearTimeout(timer);
      return { status: 0, json: null };
    });
  }

  function codeOf(r) {
    if (r.json && typeof r.json.code === 'string') return r.json.code;
    return r.status === 404 ? 'not_found' : r.status === 429 ? 'rate_limited' : r.status === 409 ? 'not_eligible' : 'server_error';
  }

  /* ---------- photo preparation (same limits as the return flow) ---------- */

  function prepare(file) {
    return new Promise(function (resolve) {
      var name = file.name || 'photo';
      var type = (file.type || '').toLowerCase();
      if (!(/^image\/(jpeg|png|heic|heif)$/.test(type) || /\.(jpe?g|png|heic|heif)$/i.test(name))) return resolve({ error: 'bad_file_type' });
      function done(blob, n) { resolve(blob.size > MAX_BYTES ? { error: 'file_too_large' } : { blob: blob, name: n }); }
      var c = document.createElement('canvas');
      if (typeof createImageBitmap !== 'function' || typeof c.toBlob !== 'function') return done(file, name);
      createImageBitmap(file).then(function (bmp) {
        var scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
        c.width = Math.max(1, Math.round(bmp.width * scale));
        c.height = Math.max(1, Math.round(bmp.height * scale));
        c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
        if (bmp.close) bmp.close();
        c.toBlob(function (b) { if (b) done(b, name.replace(/\.[^.]+$/, '') + '.jpg'); else done(file, name); }, 'image/jpeg', 0.8);
      }, function () { done(file, name); }); // e.g. HEIC the browser can't decode: send as is
    });
  }

  /* ---------- mount ---------- */

  function mount(host, order, ctx) {
    var data = order && order.parts;
    if (!data || data.available !== true) return;

    var items = Array.isArray(data.items) ? data.items : [];
    items.forEach(function (it) { if (!Array.isArray(it.parts)) it.parts = []; });
    var reasons = Array.isArray(data.reasons) ? data.reasons : [];
    var checks = data.confirmations && Array.isArray(data.confirmations.items) ? data.confirmations.items : [];
    var del = data.delivery || {};
    var origAddr = { line1: str(del.line1), line2: str(del.line2), city: str(del.city), postcode: str(del.postcode) };
    var responseTime = str(data.responseTime) || '2 working days';
    var previous = Array.isArray(data.previous) ? data.previous.slice() : [];

    var S = {
      view: 'intro', sel: {}, part: {}, other: {}, reason: {}, photos: {}, photoMsg: {}, ack: {}, note: '',
      addrEdit: !(origAddr.line1.trim() && origAddr.city.trim()), addr: { line1: origAddr.line1, line2: origAddr.line2, city: origAddr.city, postcode: origAddr.postcode },
      phone: str(del.phone), sending: false, result: null,
      requestId: '', requestKey: '', msg: '', photoSeq: 0
    };

    host.id = 'parts';
    host.className = 'hc-card hc-return';
    while (host.firstChild) host.removeChild(host.firstChild);
    var inner = el('div', 'hc-return__inner');
    var live = el('div', 'hc-sr');
    live.setAttribute('aria-live', 'polite');
    host.appendChild(inner);
    host.appendChild(live);

    /* ----- derived state ----- */
    function otherOf(it) { return S.other[it.id] || { on: false, text: '' }; }
    function pickedParts(it) { return it.parts.filter(function (p) { return S.part[p.id] != null; }); }
    function otherCount(it) { var o = otherOf(it); return o.on && o.text.trim() ? 1 : 0; }
    function countOf(it) { return pickedParts(it).length + otherCount(it); }
    function active() { return items.filter(function (it) { return it.requestable !== false && S.sel[it.id] && countOf(it) > 0; }); }
    function totalParts() { return items.reduce(function (n, it) { return n + (it.requestable !== false && S.sel[it.id] ? countOf(it) : 0); }, 0); }
    function otherBlank() {
      return items.some(function (it) { var o = otherOf(it); return it.requestable !== false && S.sel[it.id] && o.on && !o.text.trim(); });
    }
    function reasonOf(it) { var out = null; reasons.forEach(function (r) { if (r.code === S.reason[it.id]) out = r; }); return out; }
    function doneCount(it) { return (S.photos[it.id] || []).filter(function (p) { return p.status === 'done'; }).length; }
    function hasKind(kind) { return active().some(function (it) { var r = reasonOf(it); return r && r.kind === kind; }); }
    function visibleChecks() { return checks.filter(function (c) { return !c.onlyIfReasonKind || hasKind(c.onlyIfReasonKind); }); }
    function addrOk() {
      if (!S.addrEdit) return true;
      return S.addr.line1.trim().length > 1 && S.addr.city.trim().length > 1 && UK_PC.test(S.addr.postcode.trim());
    }
    function phoneOk() { return S.phone.replace(/\D/g, '').length >= 10; }
    function curAddrString() { return addrString(S.addrEdit ? S.addr : origAddr); }
    function photoCount() { return active().reduce(function (n, it) { var r = reasonOf(it); return n + (r && r.requiresPhoto ? doneCount(it) : 0); }, 0); }

    function gateOk(view) {
      if (view === 'items') return items.some(function (it) { return it.requestable !== false && S.sel[it.id]; });
      if (view === 'parts') return totalParts() > 0 && !otherBlank();
      if (view === 'reasons') {
        var act = active();
        if (!act.length) return false;
        return act.every(function (it) {
          var r = reasonOf(it);
          if (!r) return false;
          if (r.requiresPhoto) {
            var up = (S.photos[it.id] || []).some(function (p) { return p.status === 'uploading'; });
            return doneCount(it) > 0 && !up;
          }
          return true;
        });
      }
      if (view === 'checks') return visibleChecks().every(function (c) { return S.ack[c.key]; });
      if (view === 'confirm') return addrOk() && phoneOk() && !S.sending;
      return true;
    }

    /* ----- ui helpers ----- */
    function gateBtn(b, ok) { b.setAttribute('aria-disabled', ok ? 'false' : 'true'); }
    function btn(label, cls, onClick, fk) {
      var b = el('button', 'hc-btn' + (cls ? ' ' + cls : ''), label);
      b.type = 'button';
      if (fk) b.setAttribute('data-fk', fk);
      b.addEventListener('click', function () { if (b.getAttribute('aria-disabled') === 'true') return; onClick(b); });
      return b;
    }
    function contactLink(label, cls) {
      var a = el('a', 'hc-btn' + (cls ? ' ' + cls : ''), label);
      a.href = CONTACT_URL;
      return a;
    }
    function check(on) {
      var s = el('span', 'hc-ck' + (on ? ' is-on' : ''));
      if (on) s.appendChild(svg(['M5 12l5 5 9-10'], 14, { sw: '3' }));
      return s;
    }
    function checkRow(o) {
      var b = el('button', 'hc-ret-row' + (o.disabled ? ' is-off' : '') + (o.cls ? ' ' + o.cls : ''));
      b.type = 'button';
      b.setAttribute('role', 'checkbox');
      b.setAttribute('aria-checked', o.on ? 'true' : 'false');
      if (o.disabled) b.setAttribute('aria-disabled', 'true');
      b.setAttribute('data-fk', o.fk);
      b.appendChild(check(o.on));
      if (o.lead) b.appendChild(o.lead);
      var t = el('span', 'hc-ret-row__t');
      t.appendChild(el('span', 'hc-ret-row__title', o.title));
      if (o.sub) t.appendChild(el('span', 'hc-ret-row__sub', o.sub));
      if (o.note) t.appendChild(el('span', 'hc-ret-row__note', o.note));
      b.appendChild(t);
      if (!o.disabled) b.addEventListener('click', o.toggle);
      return b;
    }
    function thumb(it) {
      var d = el('span', 'hc-ret-thumb');
      var u = safeUrl(it.imageUrl);
      if (u) { var im = document.createElement('img'); im.src = u; im.alt = ''; im.width = 56; im.height = 56; im.loading = 'lazy'; d.appendChild(im); }
      return d;
    }
    function actions() { return el('div', 'hc-ret-btns'); }
    function para(text) { return el('p', 'hc-ret-p', text); }
    function panel(cls) { return el('div', 'hc-ret-panel' + (cls ? ' ' + cls : '')); }
    function strongText(parent, strongTxt, rest) {
      parent.appendChild(el('strong', null, strongTxt));
      if (rest) parent.appendChild(document.createTextNode(rest));
    }
    function errBox(text) { var m = el('div', 'hc-error', text); m.setAttribute('role', 'alert'); return m; }

    /* ----- navigation ----- */
    function stepIndex() { return STEP_KEYS.indexOf(S.view); }
    function go(view, focusHeading) {
      S.view = view;
      S.msg = '';
      render(focusHeading !== false);
      var i = STEP_KEYS.indexOf(view);
      live.textContent = '';
      if (i > -1) setTimeout(function () { live.textContent = 'Step ' + (i + 1) + ' of 5: ' + STEPS[i]; }, 60);
    }

    /* ----- views ----- */
    function viewIntro(body) {
      body.appendChild(para('A part missing, damaged or not right? Tell us which part you need, using the names from your assembly guide, and add a photo if we ask for one. We’ll look into it and, if it’s approved, send the part free of charge.'));
      var p = panel();
      p.appendChild(document.createTextNode('We reply within '));
      p.appendChild(el('strong', null, responseTime));
      body.appendChild(p);
      var a = actions();
      a.appendChild(btn('Request a part', 'hc-btn--primary', function () { go('items'); }, 'start'));
      a.appendChild(contactLink('Contact us instead'));
      body.appendChild(a);

      if (previous.length) {
        body.appendChild(el('h3', 'hc-gd hc-ret-next__title', 'Your earlier requests'));
        var list = el('div', 'hc-ret-sumlist');
        previous.forEach(function (pr) {
          var row = el('div', 'hc-ret-sumrow');
          var l = el('div');
          var head = el('div', 'hc-ret-row__title', str(pr.reference));
          if (str(pr.createdAt)) head.appendChild(el('span', 'hc-ret-muted', ' · ' + str(pr.createdAt)));
          l.appendChild(head);
          (Array.isArray(pr.items) ? pr.items : []).forEach(function (pi) {
            var names = (Array.isArray(pi.parts) ? pi.parts : []).map(function (pp) { return str(pp.name) + (pp.qty > 1 ? ' × ' + pp.qty : ''); }).join(', ');
            l.appendChild(el('div', 'hc-ret-row__sub', str(pi.title) + (names ? ': ' + names : '')));
          });
          row.appendChild(l);
          if (str(pr.statusLabel)) row.appendChild(el('span', 'hc-statuschip', str(pr.statusLabel)));
          list.appendChild(row);
        });
        body.appendChild(list);
      }
    }

    function viewItems(body) {
      body.appendChild(para('Which products is the part for? You’ll choose the parts next.'));
      var g = el('div', 'hc-ret-group');
      g.setAttribute('role', 'group');
      g.setAttribute('aria-label', 'Products');
      items.forEach(function (it) {
        var ok = it.requestable !== false && it.parts.length > 0;
        g.appendChild(checkRow({
          fk: 'item-' + it.id, on: ok && !!S.sel[it.id], disabled: !ok, lead: thumb(it),
          title: str(it.title), sub: str(it.variant), note: str(it.note),
          toggle: function () { if (S.sel[it.id]) delete S.sel[it.id]; else S.sel[it.id] = true; render(false); }
        }));
      });
      body.appendChild(g);
      var n = items.filter(function (it) { return it.requestable !== false && S.sel[it.id]; }).length;
      body.appendChild(el('div', 'hc-ret-sum', n ? n + ' selected · choose the parts next' : 'Select at least one product to continue.'));
      var a = actions();
      var next = btn('Continue', 'hc-btn--primary', function () { go('parts'); }, 'next');
      gateBtn(next, gateOk('items'));
      a.appendChild(next);
      a.appendChild(btn('Cancel', '', function () { go('intro'); }, 'cancel'));
      body.appendChild(a);
    }

    function qtyStepper(it, p) {
      var cur = S.part[p.id];
      var wrap = el('div', 'hc-qty');
      wrap.appendChild(el('span', 'hc-qty__label', 'How many?'));
      var minus = el('button', 'hc-qty__btn', '−');
      minus.type = 'button';
      minus.setAttribute('aria-label', 'Decrease quantity of ' + str(p.name));
      minus.setAttribute('data-fk', 'qm-' + p.id);
      if (cur <= 1) minus.setAttribute('aria-disabled', 'true');
      minus.addEventListener('click', function () { if (minus.getAttribute('aria-disabled') === 'true') return; S.part[p.id] = cur - 1; render(false); });
      var val = el('span', 'hc-qty__val', String(cur));
      val.setAttribute('aria-live', 'polite');
      var plus = el('button', 'hc-qty__btn', '+');
      plus.type = 'button';
      plus.setAttribute('aria-label', 'Increase quantity of ' + str(p.name));
      plus.setAttribute('data-fk', 'qp-' + p.id);
      if (cur >= p.qty) plus.setAttribute('aria-disabled', 'true');
      plus.addEventListener('click', function () { if (plus.getAttribute('aria-disabled') === 'true') return; S.part[p.id] = cur + 1; render(false); });
      wrap.appendChild(minus);
      wrap.appendChild(val);
      wrap.appendChild(plus);
      return wrap;
    }

    function viewParts(body) {
      body.appendChild(para('Tick the parts you need. The names match your assembly guide. Where there’s more than one of a part, choose how many.'));
      var next = btn('Continue', 'hc-btn--primary', function () { go('reasons'); }, 'next');
      function refreshNext() {
        gateBtn(next, gateOk('parts'));
        sumEl.textContent = totalParts() > 0 ? plural(totalParts(), 'part', 'parts') + ' selected' : 'Select at least one part to continue. A product with no parts selected is left out.';
      }
      var sumEl = el('div', 'hc-ret-sum hc-ret-sum--plain');

      items.filter(function (it) { return it.requestable !== false && S.sel[it.id]; }).forEach(function (it) {
        var card = el('div', 'hc-ret-card');
        var head = el('div', 'hc-parts-head');
        head.appendChild(el('div', 'hc-ret-card__title', str(it.title)));
        var n = countOf(it);
        head.appendChild(el('span', 'hc-parts-count', n ? plural(n, 'part', 'parts') : 'none selected yet'));
        card.appendChild(head);

        var g = el('div', 'hc-ret-group');
        g.setAttribute('role', 'group');
        g.setAttribute('aria-label', 'Parts for ' + str(it.title));
        var curPack = null;
        it.parts.forEach(function (p) {
          if (p.packNo == null) { curPack = null; }
          else if (p.packCount > 1 && p.packNo !== curPack) {
            curPack = p.packNo;
            g.appendChild(el('div', 'hc-pack', 'Pack ' + str(p.packNo) + ' of ' + str(p.packCount)));
          }
          var on = S.part[p.id] != null;
          var row = el('div', 'hc-part');
          var badge = badgeFor(p);
          var lead = badge ? el('span', 'hc-part__badge', badge) : null;
          row.appendChild(checkRow({
            fk: 'part-' + p.id, on: on, lead: lead, title: str(p.name), cls: 'hc-part__btn',
            toggle: function () { if (on) delete S.part[p.id]; else S.part[p.id] = 1; render(false); }
          }));
          if (on && p.qty > 1) row.appendChild(qtyStepper(it, p));
          g.appendChild(row);
        });

        // Always last: another part, or not sure.
        var o = otherOf(it);
        var orow = el('div', 'hc-part hc-part--other');
        orow.appendChild(checkRow({
          fk: 'other-' + it.id, on: o.on, title: 'Another part, or not sure', cls: 'hc-part__btn hc-part__other',
          toggle: function () { S.other[it.id] = { on: !o.on, text: o.text }; render(false); }
        }));
        if (o.on) {
          var lb = el('label', 'hc-field hc-part__describe');
          lb.appendChild(document.createTextNode('Describe it'));
          var inp = document.createElement('input');
          inp.type = 'text'; inp.value = o.text; inp.maxLength = 200; inp.autocomplete = 'off';
          inp.placeholder = 'e.g. small black cap on the left rail';
          inp.setAttribute('data-fk', 'other-text-' + it.id);
          inp.addEventListener('input', function () { S.other[it.id] = { on: true, text: inp.value }; refreshNext(); });
          lb.appendChild(inp);
          orow.appendChild(lb);
        }
        g.appendChild(orow);
        card.appendChild(g);
        body.appendChild(card);
      });

      body.appendChild(sumEl);
      var a = actions();
      a.appendChild(next);
      a.appendChild(btn('Back', '', function () { go('items'); }, 'back'));
      body.appendChild(a);
      refreshNext();
    }

    function viewReasons(body) {
      body.appendChild(para('What happened with each product? It helps us sort it quickly.'));
      if (S.msg) body.appendChild(errBox(S.msg));
      var next = btn('Continue', 'hc-btn--primary', function () { go('checks'); }, 'next');
      function refreshNext() { gateBtn(next, gateOk('reasons')); }
      active().forEach(function (it) { body.appendChild(reasonPanel(it, refreshNext)); });

      var lab = el('label', 'hc-field');
      lab.appendChild(document.createTextNode('Anything else we should know? (optional)'));
      var inp = document.createElement('input');
      inp.type = 'text'; inp.value = S.note; inp.maxLength = 500; inp.autocomplete = 'off';
      inp.placeholder = 'e.g. the box looked crushed when it arrived';
      inp.addEventListener('input', function () { S.note = inp.value; });
      lab.appendChild(inp);
      body.appendChild(lab);

      var a = actions();
      refreshNext();
      a.appendChild(next);
      a.appendChild(btn('Back', '', function () { go('parts'); }, 'back'));
      body.appendChild(a);
    }

    function reasonPanel(it, refreshNext) {
      var p = el('div', 'hc-ret-card hc-ret-card--stack');
      var head = el('div');
      head.appendChild(el('div', 'hc-ret-card__title', str(it.title)));
      head.appendChild(el('div', 'hc-ret-row__sub', plural(countOf(it), 'part', 'parts')));
      p.appendChild(head);

      var rg = el('div', 'hc-chips');
      rg.setAttribute('role', 'radiogroup');
      rg.setAttribute('aria-label', 'What happened with ' + str(it.title));
      var chips = [];
      reasons.forEach(function (r) {
        var c = el('button', 'hc-chip', str(r.label));
        c.type = 'button';
        c.setAttribute('role', 'radio');
        c.setAttribute('data-fk', 'reason-' + it.id + '-' + r.code);
        c.setAttribute('aria-checked', S.reason[it.id] === r.code ? 'true' : 'false');
        c.addEventListener('click', function () {
          S.reason[it.id] = r.code;
          chips.forEach(function (x) { x.el.setAttribute('aria-checked', x.code === r.code ? 'true' : 'false'); });
          fill();
          refreshNext();
        });
        chips.push({ code: r.code, el: c });
        rg.appendChild(c);
      });
      p.appendChild(rg);

      var zone = el('div', 'hc-ret-zone');
      zone.setAttribute('role', 'status');
      p.appendChild(zone);
      var photoBox = el('div', 'hc-ret-note hc-ret-photos');
      photoBox.hidden = true;
      p.appendChild(photoBox);

      function fill() {
        var r = reasonOf(it);
        while (zone.firstChild) zone.removeChild(zone.firstChild);
        zone.className = 'hc-ret-zone';
        if (r && r.requiresPhoto) {
          zone.className = 'hc-ret-zone hc-ret-note';
          if (r.kind === 'missing') strongText(zone, 'Please lay out all the parts you do have.', ' Photograph everything from the box together on the floor, so we can see what is there and what isn’t.');
          else if (r.kind === 'damaged') strongText(zone, 'We’re sorry. Please add a photo as proof.', ' Show the damage, and the part number on the part if you can see it.');
          else zone.appendChild(document.createTextNode('Please add a photo so we can see what’s happened.'));
        }
        photoBox.hidden = !(r && r.requiresPhoto);
        if (r && r.requiresPhoto) buildPhotos();
      }

      var fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif';
      fileInput.multiple = true;
      fileInput.hidden = true;
      fileInput.setAttribute('tabindex', '-1');
      fileInput.setAttribute('aria-hidden', 'true');
      p.appendChild(fileInput);
      fileInput.addEventListener('change', function () {
        var files = [].slice.call(fileInput.files || []);
        fileInput.value = '';
        files.forEach(function (f) { addPhoto(f); });
      });

      function setMsg(m) { S.photoMsg[it.id] = m || ''; buildPhotos(); }

      function addPhoto(file) {
        var list = S.photos[it.id] = S.photos[it.id] || [];
        if (list.length >= MAX_PHOTOS) { setMsg(PHOTO_ERR.too_many_photos); return; }
        var rec = { key: ++S.photoSeq, name: file.name || 'photo.jpg', id: null, status: 'uploading' };
        list.push(rec);
        S.photoMsg[it.id] = '';
        buildPhotos();
        refreshNext();
        function fail(code) {
          S.photos[it.id] = (S.photos[it.id] || []).filter(function (x) { return x !== rec; });
          S.photoMsg[it.id] = PHOTO_ERR[code] || PHOTO_ERR.not_found;
          buildPhotos();
          refreshNext();
        }
        prepare(file).then(function (prep) {
          if (prep.error) return fail(prep.error);
          rec.name = prep.name;
          var fd = new FormData();
          fd.append('order', ctx.orderNo());
          fd.append('email', ctx.email());
          fd.append('itemId', it.id);
          fd.append('file', prep.blob, prep.name);
          send('photo', EP.photo, isMock() ? { itemId: it.id } : fd, !isMock()).then(function (r) {
            if (r.status === 200 && r.json && r.json.ok === true && r.json.photoId) {
              rec.id = String(r.json.photoId);
              rec.status = 'done';
              buildPhotos();
              refreshNext();
            } else { fail(codeOf(r)); }
          });
        });
      }

      function buildPhotos() {
        while (photoBox.firstChild) photoBox.removeChild(photoBox.firstChild);
        var list = S.photos[it.id] || [];
        if (list.length) {
          var chipsEl = el('div', 'hc-ret-photolist');
          list.forEach(function (ph) {
            var c = el('span', 'hc-photo');
            c.appendChild(el('span', 'hc-photo__name', ph.name));
            if (ph.status === 'uploading') c.appendChild(el('span', 'hc-photo__state', 'Uploading…'));
            var rm = el('button', 'hc-photo__rm', '×');
            rm.type = 'button';
            rm.setAttribute('aria-label', 'Remove ' + ph.name);
            rm.setAttribute('data-fk', 'rm-' + ph.key);
            rm.addEventListener('click', function () {
              S.photos[it.id] = (S.photos[it.id] || []).filter(function (x) { return x !== ph; });
              S.photoMsg[it.id] = '';
              buildPhotos();
              refreshNext();
            });
            c.appendChild(rm);
            chipsEl.appendChild(c);
          });
          photoBox.appendChild(chipsEl);
        }
        var full = list.length >= MAX_PHOTOS;
        var add = el('button', 'hc-btn hc-btn--white');
        add.type = 'button';
        add.setAttribute('data-fk', 'add-' + it.id);
        add.appendChild(svg(['M4 8h3l2-3h6l2 3h3v11H4z', 'M12 9.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z'], 18));
        add.appendChild(document.createTextNode(' ' + (full ? 'Maximum 5 photos' : (list.length ? 'Add another photo' : 'Add a photo'))));
        if (full) add.setAttribute('aria-disabled', 'true');
        add.addEventListener('click', function () { if (add.getAttribute('aria-disabled') === 'true') return; fileInput.click(); });
        var wrap = el('div');
        wrap.appendChild(add);
        photoBox.appendChild(wrap);
        photoBox.appendChild(el('div', 'hc-ret-hint', 'Up to 5 photos, JPG, PNG or HEIC.'));
        if (S.photoMsg[it.id]) photoBox.appendChild(errBox(S.photoMsg[it.id]));
      }

      fill();
      return p;
    }

    function viewChecks(body) {
      body.appendChild(para('Before we send your request, please check these. Tick each one to confirm.'));
      if (S.msg) body.appendChild(errBox(S.msg));
      var vis = visibleChecks();
      var next = btn('I understand, continue', 'hc-btn--primary', function () { go('confirm'); }, 'next');
      var g = el('div', 'hc-ret-group');
      g.setAttribute('role', 'group');
      g.setAttribute('aria-label', 'Checks');
      vis.forEach(function (c) {
        g.appendChild(checkRow({
          fk: 'check-' + c.key, on: !!S.ack[c.key], title: str(c.title), sub: str(c.text),
          toggle: function () { S.ack[c.key] = !S.ack[c.key]; render(false); }
        }));
      });
      body.appendChild(g);
      var a = actions();
      gateBtn(next, gateOk('checks'));
      a.appendChild(next);
      a.appendChild(btn('Back', '', function () { go('reasons'); }, 'back'));
      body.appendChild(a);
    }

    function partLine(it, p) {
      var b = badgeFor(p);
      return S.part[p.id] + ' × ' + str(p.name) + (b ? ' (part ' + b + ')' : '');
    }

    function viewConfirm(body) {
      body.appendChild(para('Check everything looks right, then send your request.'));
      var sum = el('div', 'hc-ret-sumlist');
      active().forEach(function (it) {
        var r = reasonOf(it);
        var row = el('div', 'hc-ret-sumrow');
        var l = el('div');
        l.appendChild(el('div', 'hc-ret-row__title', str(it.title)));
        pickedParts(it).forEach(function (p) { l.appendChild(el('div', 'hc-ret-row__sub', partLine(it, p))); });
        if (otherCount(it)) l.appendChild(el('div', 'hc-ret-row__sub', 'Another part: ' + otherOf(it).text.trim()));
        l.appendChild(el('div', 'hc-ret-extra', r ? str(r.label) : ''));
        if (r && r.requiresPhoto) { var n = doneCount(it); l.appendChild(el('div', 'hc-ret-extra', n + (n === 1 ? ' photo attached' : ' photos attached'))); }
        row.appendChild(l);
        sum.appendChild(row);
      });
      body.appendChild(sum);

      var cp = el('div', 'hc-ret-card hc-ret-card--stack');
      var top = el('div', 'hc-ret-coltop');
      var left = el('div', 'hc-ret-colleft');
      var pin = el('span', 'hc-ret-boxico');
      pin.appendChild(svg(['M12 21s7-6.2 7-11a7 7 0 0 0-14 0c0 4.8 7 11 7 11z', 'M12 7.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z'], 20));
      left.appendChild(pin);
      var lt = el('div');
      lt.appendChild(el('div', 'hc-ret-row__title', 'We’ll send the parts to'));
      var addrLine = el('div', 'hc-ret-addr', curAddrString());
      lt.appendChild(addrLine);
      var changed = S.addrEdit && origAddr.line1.trim();
      lt.appendChild(el('div', 'hc-ret-row__sub', changed ? 'Changed for this request' : 'Delivery address on your order'));
      left.appendChild(lt);
      top.appendChild(left);
      var tog = el('button', 'hc-btn hc-btn--white', changed ? 'Use delivery address' : 'Change address');
      tog.type = 'button';
      tog.setAttribute('aria-expanded', S.addrEdit ? 'true' : 'false');
      tog.setAttribute('data-fk', 'addr-toggle');
      tog.addEventListener('click', function () {
        if (S.addrEdit && origAddr.line1.trim() && origAddr.city.trim()) {
          S.addr = { line1: origAddr.line1, line2: origAddr.line2, city: origAddr.city, postcode: origAddr.postcode };
          S.addrEdit = false;
        } else { S.addrEdit = true; }
        render(false);
      });
      top.appendChild(tog);
      cp.appendChild(top);

      var send1 = btn('Send my request', 'hc-btn--primary', doSend, 'send');
      var addrErr = el('div', 'hc-error', ADDR_COPY);
      addrErr.setAttribute('role', 'alert');
      var phoneHint = el('div', 'hc-field__help hc-err-text', 'Please enter a phone number with at least 10 digits.');
      var phoneTouched = false;
      function refreshSend() {
        gateBtn(send1, gateOk('confirm'));
        addrErr.hidden = !S.addrEdit || addrOk();
        phoneHint.hidden = !(phoneTouched && !phoneOk());
        addrLine.textContent = curAddrString();
      }

      if (S.addrEdit) {
        var form = el('div', 'hc-ret-addrform');
        [['line1', 'Address line 1', 'address-line1', '14 Orchard Road'], ['line2', 'Address line 2 (optional)', 'address-line2', 'Flat, building, access notes'],
          ['city', 'Town or city', 'address-level2', 'London'], ['postcode', 'Postcode', 'postal-code', 'E8 3AB']].forEach(function (d) {
          var lb = el('label', 'hc-field');
          lb.appendChild(document.createTextNode(d[1]));
          var i = document.createElement('input');
          i.type = 'text'; i.value = S.addr[d[0]]; i.autocomplete = d[2]; i.placeholder = d[3]; i.maxLength = 120;
          if (d[0] === 'postcode') { i.autocapitalize = 'characters'; i.spellcheck = false; }
          i.addEventListener('input', function () { S.addr[d[0]] = i.value; refreshSend(); });
          lb.appendChild(i);
          form.appendChild(lb);
        });
        form.appendChild(addrErr);
        form.appendChild(el('div', 'hc-field__help', 'UK addresses only.'));
        cp.appendChild(form);
      }

      var pl = el('label', 'hc-field');
      pl.appendChild(document.createTextNode('Phone number for the courier'));
      var pi = document.createElement('input');
      pi.type = 'tel'; pi.inputMode = 'tel'; pi.autocomplete = 'tel'; pi.value = S.phone; pi.placeholder = '07700 900123'; pi.maxLength = 30;
      pi.addEventListener('input', function () { S.phone = pi.value; phoneTouched = true; refreshSend(); });
      pi.addEventListener('blur', function () { phoneTouched = true; refreshSend(); });
      pl.appendChild(pi);
      pl.appendChild(phoneHint);
      cp.appendChild(pl);
      body.appendChild(cp);

      if (S.msg) body.appendChild(errBox(S.msg));

      var a = actions();
      if (S.sending) send1.textContent = 'Sending…';
      a.appendChild(send1);
      a.appendChild(btn('Back', '', function () { go('checks'); }, 'back'));
      body.appendChild(a);
      refreshSend();
    }

    function buildPayload() {
      var coll = S.addrEdit ? S.addr : origAddr;
      return {
        order: ctx.orderNo(),
        email: ctx.email(),
        items: active().map(function (it) {
          var list = pickedParts(it).map(function (p) { return { partId: p.id, qty: S.part[p.id] }; });
          if (otherCount(it)) list.push({ name: otherOf(it).text.trim() });
          return { id: it.id, reason: reasonOf(it).code, parts: list };
        }),
        note: S.note.trim(),
        delivery: { line1: coll.line1.trim(), line2: coll.line2.trim(), city: coll.city.trim(), postcode: coll.postcode.trim().toUpperCase(), phone: S.phone.trim() },
        confirmationsVersion: str(data.confirmations && data.confirmations.version),
        acknowledged: visibleChecks().filter(function (c) { return S.ack[c.key]; }).map(function (c) { return c.key; })
      };
    }

    function doSend() {
      if (S.sending || !gateOk('confirm')) return;
      var payload = buildPayload();
      var key = JSON.stringify({ i: payload.items, d: payload.delivery, v: payload.confirmationsVersion, a: payload.acknowledged, n: payload.note });
      if (!S.requestId || S.requestKey !== key) { S.requestId = uuid(); S.requestKey = key; }
      payload.requestId = S.requestId;
      var damaged = hasKind('damaged');
      var snapshot = active().map(function (it) {
        return { title: str(it.title), parts: pickedParts(it).map(function (p) { return { name: str(p.name), qty: S.part[p.id] }; })
          .concat(otherCount(it) ? [{ name: otherOf(it).text.trim(), qty: 1 }] : []) };
      });
      if (isMock()) payload.__hasPhotos = photoCount() > 0; // mock only: lets the fixture echo hasPhotos
      S.sending = true;
      S.msg = '';
      render(false);
      send('request', EP.request, payload).then(function (r) {
        S.sending = false;
        var j = r.json;
        if (r.status === 200 && j && j.ok === true) {
          S.result = { reference: str(j.reference), deliveryAddress: str(j.deliveryAddress) || curAddrString(), hasPhotos: j.hasPhotos === true, damaged: damaged, snapshot: snapshot };
          go('sent');
          return;
        }
        var code = codeOf(r);
        if (code === 'not_eligible') { S.msg = INELIGIBLE_COPY; go('confirm', false); return; }
        if (code === 'invalid_address') { S.addrEdit = true; S.msg = ADDR_COPY; go('confirm', false); return; }
        if (code === 'photo_required') { S.msg = 'Please add a photo for each product that needs one, then continue.'; go('reasons'); return; }
        if (code === 'confirmation_missing') { S.msg = 'Please tick each check to continue.'; go('checks'); return; }
        if (code === 'rate_limited') { S.msg = RATE_COPY; go('confirm', false); return; }
        go('error');
      });
    }

    function resetFlow() {
      S.sel = {}; S.part = {}; S.other = {}; S.reason = {}; S.photos = {}; S.photoMsg = {}; S.ack = {};
      S.note = ''; S.requestId = ''; S.requestKey = ''; S.msg = ''; S.sending = false;
    }

    function viewSent(body) {
      var r = S.result || {};
      var p = el('div', 'hc-ret-lead');
      if (r.reference) p.appendChild(document.createTextNode('Request reference ' + str(r.reference) + '.'));
      body.appendChild(p);
      if (r.hasPhotos === true) {
        var ph = panel();
        strongText(ph, 'We’ve got your photos.', ' Our team will review them and be in touch.');
        body.appendChild(ph);
      }
      var nx = el('div', 'hc-ret-panel hc-ret-panel--stack');
      nx.appendChild(el('h3', 'hc-gd hc-ret-next__title', 'What happens next'));
      var ol = el('ol', 'hc-ret-next');
      ol.appendChild(el('li', null, 'We review your request and reply by email within ' + responseTime + '.'));
      ol.appendChild(el('li', null, 'If it’s approved, we send the parts free of charge to ' + str(r.deliveryAddress) + '.'));
      if (r.damaged) ol.appendChild(el('li', null, 'Please keep any damaged parts. Sending them back is optional, and if we need them we’ll arrange it after you’ve received your replacement.'));
      nx.appendChild(ol);
      body.appendChild(nx);
      var a = actions();
      a.appendChild(btn('Back to my order', 'hc-btn--primary', function () {
        if (r.reference) {
          previous.unshift({ reference: r.reference, createdAt: 'Today', status: 'SUBMITTED', statusLabel: 'Received', items: r.snapshot || [] });
        }
        resetFlow();
        S.result = null;
        go('intro');
        var top = document.querySelector('[data-hc-orderview]');
        if (top && top.scrollIntoView) top.scrollIntoView({ block: 'start' });
      }, 'back-order'));
      a.appendChild(contactLink('Contact us'));
      body.appendChild(a);
    }

    function viewError(body) {
      var e = el('div', 'hc-delay');
      e.setAttribute('role', 'alert');
      e.textContent = 'Something went wrong on our side and your request wasn’t sent. Your choices are saved, so you can try again. If it keeps happening, message us.';
      body.appendChild(e);
      var a = actions();
      a.appendChild(btn('Try again', 'hc-btn--primary', function () { go('confirm'); }, 'retry'));
      a.appendChild(contactLink('Contact us'));
      body.appendChild(a);
    }

    var VIEWS = { intro: viewIntro, items: viewItems, parts: viewParts, reasons: viewReasons, checks: viewChecks, confirm: viewConfirm, sent: viewSent, error: viewError };

    function render(focusHeading) {
      var activeKey = document.activeElement && host.contains(document.activeElement) ? document.activeElement.getAttribute('data-fk') : null;
      while (inner.firstChild) inner.removeChild(inner.firstChild);
      var idx = stepIndex();
      var head = el('div', 'hc-return__head');
      var h2 = el('h2', 'hc-gd hc-return__title', TITLES[S.view] || TITLES.intro);
      h2.tabIndex = -1;
      head.appendChild(h2);
      if (idx > -1) head.appendChild(el('span', 'hc-return__step', 'Step ' + (idx + 1) + ' of 5'));
      inner.appendChild(head);
      if (idx > -1) {
        var st = el('div', 'hc-stepper');
        STEPS.forEach(function (label, i) {
          var seg = el('div', 'hc-stepper__seg' + (i <= idx ? ' is-on' : ''));
          seg.appendChild(el('div', 'hc-stepper__line'));
          seg.appendChild(el('div', 'hc-stepper__label', label));
          st.appendChild(seg);
        });
        inner.appendChild(st);
      }
      var body = el('div', 'hc-return__body');
      (VIEWS[S.view] || viewIntro)(body);
      inner.appendChild(body);
      if (focusHeading) { h2.focus(); }
      else if (activeKey) {
        var t = inner.querySelector('[data-fk="' + activeKey.replace(/"/g, '') + '"]');
        if (t) t.focus();
      }
    }

    /* ----- mock presets: jump straight to a view (matches the design's Tweaks panel) ----- */
    function applyPreset() {
      var p = isMock() ? qs('parts') : '';
      if (!p || p === 'intro' || p === 'earlier') return;
      S.sel['it-bed'] = true; S.sel['it-drawers'] = true;
      if (p === 'items') { S.sel = {}; S.view = 'items'; return; }
      if (p === 'parts') { S.sel = { 'it-bed': true }; S.view = 'parts'; return; }
      S.sel = { 'it-bed': true, 'it-drawers': true };
      S.part['pt-back'] = 1; S.part['pt-side'] = 2; S.part['pt-dfront'] = 1;
      if (p === 'partsselected') { S.view = 'parts'; return; }
      var kindFor = { damaged: 'damaged', damagedphoto: 'damaged', missing: 'missing' };
      S.reason['it-bed'] = kindFor[p] === 'missing' ? 'missing' : (kindFor[p] === 'damaged' ? 'damaged' : 'faulty');
      S.reason['it-drawers'] = 'faulty';
      if (p === 'damagedphoto') S.photos['it-bed'] = [{ key: 1, name: 'IMG_2041.jpg', id: 'pp-1', status: 'done' }, { key: 2, name: 'IMG_2042.jpg', id: 'pp-2', status: 'done' }];
      if (p === 'reasons' || p === 'damaged' || p === 'damagedphoto' || p === 'missing') { S.view = 'reasons'; return; }
      S.reason['it-bed'] = 'missing';
      S.photos['it-bed'] = [{ key: 1, name: 'IMG_2041.jpg', id: 'pp-1', status: 'done' }];
      if (p === 'checks') { S.view = 'checks'; S.ack = { accuracy: true }; return; }
      S.ack = { accuracy: true, boxes: true };
      S.phone = '07700 900123';
      if (p === 'address') { S.addrEdit = true; S.addr = { line1: '22 Mill Lane', line2: 'Flat 2', city: 'Leeds', postcode: 'LS1 4AP' }; }
      if (p === 'confirm' || p === 'address') { S.view = 'confirm'; return; }
      if (p === 'sent') { S.result = { reference: 'PR-1004', deliveryAddress: addrString(origAddr), hasPhotos: true, damaged: false, snapshot: [] }; S.view = 'sent'; return; }
      if (p === 'error') { S.view = 'error'; }
    }

    applyPreset();
    render(false);
  }

  window.RoomixHelpParts = { mount: mount, mockParts: mockParts };
})();
